import hotkeys from 'hotkeys-js';
import { UAParser } from 'ua-parser-js';

import { isNil, isObject, omitNil, parseLabel, parseValue } from './utils';
import AnalyticsProviderBaseClass, {
  IDimensions,
  IEvent,
  IKeyValue,
} from './providers/analytics-provider';
import { validateReportingStandards } from './debug-validation';

import MatomoAnalyticsProvider from './providers/matomo-provider';

export {
  default as BloombergAnalyticsProvider,
  Environment,
} from './providers/bloomberg-provider';
export { default as GoogleAnalyticsProvider } from './providers/google-provider';
export { default as GoogleTagManagerAnalyticsProvider } from './providers/google-tag-manager-provider';
export { default as MatomoAnalyticsProvider } from './providers/matomo-provider';
export { default as DummyAnalyticsProvider } from './providers/dummy-provider';
export { NamespaceType } from './providers/analytics-provider';
export const AnalyticsProvider = AnalyticsProviderBaseClass;

declare global {
  interface Window {
    bbAnalytics: {
      initialized: boolean;
      providers: AnalyticsProviderBaseClass[];
      debug: boolean;
      debugWarnOnly: boolean;
      listenToJSErrors: boolean;
    };
  }
}

function loopThroughProviders(
  providers: AnalyticsProviderBaseClass[],
  cb: (provider: AnalyticsProviderBaseClass) => any
) {
  let hasCalledMatomo = false;
  providers.forEach((provider: AnalyticsProviderBaseClass) => {
    if (provider instanceof MatomoAnalyticsProvider) {
      if (!hasCalledMatomo) {
        cb(provider);
        hasCalledMatomo = true;
      }
    } else {
      cb(provider);
    }
  });
}

function initializedOrError() {
  if (!window?.bbAnalytics?.initialized) {
    throw new Error(
      'bb-analytics has not been initialized with a analytics provider. Please call the initialize method before setting dimensions, options, or event tracking.'
    );
  }
  return window.bbAnalytics.initialized;
}

function debugWarning(message: string) {
  const bbAnalytics = window.bbAnalytics;
  if (bbAnalytics.debug) {
    if (bbAnalytics.debugWarnOnly) {
      console.warn(message); // eslint-disable-line no-console
    } else {
      throw new Error(message);
    }
  }
}

function validateEventParamsInDebug(props: IEvent) {
  const bbAnalytics = window.bbAnalytics;
  if (bbAnalytics.debug) {
    for (let [key, value] of Object.entries(props)) {
      validateReportingStandards({
        key,
        value,
        safe: bbAnalytics.debugWarnOnly,
      });
    }
  }
}

function initializeProvider(
  provider: AnalyticsProviderBaseClass | AnalyticsProviderBaseClass[]
) {
  const providers = Array.isArray(provider) ? provider : [provider];

  for (let i = 0; i < providers.length; i++) {
    const provider = providers[i];
    if (!(provider instanceof AnalyticsProvider)) {
      debugWarning(
        'bb-analytics initialized with provider that does not inherit from AnalyticsProvider.'
      );
      return {
        result: false,
        failedProvider: provider,
      };
    } else {
      const isAbleToInitialize = provider.initialize();
      if (!isAbleToInitialize) {
        return {
          result: false,
          failedProvider: provider,
        };
      }
    }
  }
  return {
    result: true,
    failedProvider: undefined,
  };
}

function sendEvent(
  { category, action, label, value, namespace }: IEvent,
  trackerNames?: string[]
) {
  if (!category) {
    throw new Error('You must provide a category for your analytics event.');
  } else if (!action) {
    throw new Error('You must provide an action for your analytics event.');
  } else {
    const args = omitNil(
      parseValue(parseLabel({ category, action, label, value, namespace }))
    ) as IEvent;

    loopThroughProviders(
      window.bbAnalytics.providers,
      (provider: AnalyticsProviderBaseClass) => {
        provider.event(args, trackerNames);
      }
    );
  }
}

/*** PUBLIC API ***/
export function setDimension(
  dimensions: IDimensions,
  trackerNames?: string[]
): void {
  if (initializedOrError()) {
    // only set dimensions for matomo once given that it can only be initialized once
    loopThroughProviders(
      window.bbAnalytics.providers,
      (provider: AnalyticsProviderBaseClass) =>
        provider.setDimension(dimensions, trackerNames)
    );
  }
  return undefined;
}

export function setOption(key: string | IKeyValue, value?: any) {
  if (initializedOrError()) {
    if (isObject(key)) {
      key = key as object;
      for (const k in key) {
        setOption(k, key[k]);
      }
    } else if (!isNil(value)) {
      switch (key) {
        case 'debug':
          window.bbAnalytics.debug = value;
          break;
        case 'debugWarnOnly':
          window.bbAnalytics.debugWarnOnly = value;
          break;
        case 'listenToJSErrors':
          window.bbAnalytics.listenToJSErrors = value;
          break;
        default:
          debugWarning(
            `You have passed an unsupported options key to bb-analytics: ${key}`
          );
      }
    }
  }
  return undefined;
}

export function deInitialize() {
  // @ts-ignore
  if (!window.bbAnalytics) window.bbAnalytics = {};

  window.bbAnalytics.initialized = false;
  window.bbAnalytics.providers = [];
  window.bbAnalytics.debug = false;
  window.bbAnalytics.debugWarnOnly = false;
  window.bbAnalytics.listenToJSErrors = false;
}

export function createBrowserHistoryPageTracker({
  includeQuery = false,
  namespace = undefined,
}: {
  includeQuery?: boolean;
  namespace?: undefined | null | string;
} = {}) {
  return (
    newRoute: string | Location,
    trackerNames?: string[],
    title?: string
  ) => {
    if (initializedOrError()) {
      const bbAnalytics = window.bbAnalytics;
      if (typeof newRoute === 'string') {
        const [pathname, search] = newRoute.split('?');
        loopThroughProviders(
          bbAnalytics.providers,
          (provider: AnalyticsProviderBaseClass) =>
            provider.pageView(
              `${pathname}${includeQuery && search ? `?${search}` : ''}`,
              namespace,
              trackerNames,
              title
            )
        );
      } else if (newRoute && newRoute.pathname) {
        loopThroughProviders(
          bbAnalytics.providers,
          (provider: AnalyticsProviderBaseClass) =>
            provider.pageView(
              `${newRoute.pathname}${includeQuery ? newRoute.search : ''}`,
              namespace,
              trackerNames,
              title
            )
        );
      }
    }
  };
}

export function createEventTracker(
  props: Partial<IEvent> = {},
  trackerNames?: string[]
) {
  return (actionOrProps: string | Partial<IEvent>) => {
    if (initializedOrError()) {
      if (typeof actionOrProps === 'string') {
        const { category, label, value, namespace } = props;
        validateEventParamsInDebug({
          action: actionOrProps,
          category: category as string,
          label,
        });
        return sendEvent(
          {
            action: actionOrProps,
            category: category as string,
            label,
            value,
            namespace,
          },
          trackerNames
        );
      }
      const { category, action, label, value, namespace } = {
        ...props,
        ...actionOrProps,
      };
      validateEventParamsInDebug({
        action: action as string,
        category: category as string,
        label,
      });
      return sendEvent(
        {
          action: action as string,
          category: category as string,
          label,
          value,
          namespace,
        },
        trackerNames
      );
    }
    return undefined;
  };
}

function initializeBrowserEvents() {
  const onTrackEvent = createEventTracker({ category: 'browserCommand' });
  const isSafari = () =>
    new UAParser(window.navigator.userAgent)
      .getBrowser()
      .name?.includes('Safari');
  const browserFullPath = () => {
    const { pathname, search } = window.location;
    return [pathname, search].filter(item => item).join('');
  };

  // Global Print Events
  if (isSafari()) {
    const mediaQueryList = window.matchMedia('print');
    mediaQueryList.addListener(mql => {
      if (mql.matches) {
        onTrackEvent({
          action: 'initiatePrint',
          label: browserFullPath(),
        });
      }
    });
  } else {
    window.addEventListener('beforeprint', () =>
      onTrackEvent({
        action: 'initiatePrint',
        label: browserFullPath(),
      })
    );
  }

  // Global In-Page Find Events
  hotkeys('ctrl+f,command+f', () => {
    onTrackEvent({
      action: 'inPageFind',
      label: browserFullPath(),
    });
  });
}

// Inspired by https://github.com/matomo-org/matomo/blob/2.16.0/js/piwik.js#L5869-L5886
export function enableJSErrorTracking() {
  if (window.bbAnalytics.listenToJSErrors) {
    return;
  }
  window.bbAnalytics.listenToJSErrors = true;
  window.addEventListener('error', err => {
    loopThroughProviders(
      window.bbAnalytics.providers,
      (provider: AnalyticsProviderBaseClass) => provider.error(err)
    );
  });
}

interface IInitializeProps {
  provider: AnalyticsProviderBaseClass | AnalyticsProviderBaseClass[];
  dimensions?: IDimensions;
  options?: {
    debug?: boolean;
    debugWarnOnly?: boolean;
  };
}

export function initialize({
  provider,
  dimensions,
  options = { debug: false, debugWarnOnly: false },
}: IInitializeProps) {
  if (!window?.bbAnalytics) {
    window.bbAnalytics = {
      initialized: true,
      debug: options.debug || false,
      debugWarnOnly: options.debugWarnOnly || false,
      listenToJSErrors: false,
      providers: [],
    };
  } else {
    window.bbAnalytics = {
      ...window.bbAnalytics,
      initialized: true,
      debug: options.debug || false,
      debugWarnOnly: options.debugWarnOnly || false,
    };
  }

  const { result, failedProvider } = initializeProvider(provider);
  if (result) {
    window.bbAnalytics.providers = window.bbAnalytics.providers.concat(
      provider
    );

    if (isObject(dimensions)) {
      setDimension(dimensions as IDimensions);
    }

    if (isObject(options)) {
      setOption(options);
    }

    initializeBrowserEvents();
  } else {
    if (window.bbAnalytics.providers.length === 0) {
      deInitialize();
    }
    if (failedProvider) {
      debugWarning(`Initialization failed for the provider ${failedProvider}.`);
    }
  }

  return window.bbAnalytics.initialized;
}
